import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, IconButton, InputLabel, Tooltip } from '@mui/material';
import { QcsDropdownMenu } from '../../common/basic/QcsDropdownMenu';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import styled from '@emotion/styled';
import { AxiosResponse } from 'axios';
import { AnalyticsView, AnalyticsViewToggle } from './AnalyticsViewToggle';
import { GroupByOption } from '../analyticsDomainConfig';
import { buildGroupByDropdownItems } from './buildGroupByDropdownItems';
import { ChartToolbar, ChartToolbarTrailing } from './AnalyticsChartToolbarStyles';
import { type ExportOption, type ExportScope, QcsExportButton } from '../../common/basic/QcsExportButton';

const PAGINATED_EXPORT_SCOPES = ['page', 'all'] as const satisfies readonly ExportScope[];
const UNPAGINATED_EXPORT_SCOPES = ['all'] as const satisfies readonly ExportScope[];

const COLLAPSING_BUTTON_WIDTH = 30;
const COLLAPSING_BUTTON_PADDING = 5;

export type ChartKind = 'bar' | 'pie';

const collapsingButtonStyles = (visible: boolean) => ({
  opacity: visible ? 1 : 0,
  transform: visible ? 'scale(1)' : 'scale(0.5)',
  transition:
    'opacity 250ms ease, transform 250ms cubic-bezier(0.34, 1.56, 0.64, 1), width 250ms ease, padding 250ms ease',
  pointerEvents: (visible ? 'auto' : 'none') as 'auto' | 'none',
  width: visible ? COLLAPSING_BUTTON_WIDTH : 0,
  padding: visible ? COLLAPSING_BUTTON_PADDING : 0,
  overflow: 'hidden' as const,
});

const WarningIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible: boolean }>(({ theme, visible }) => ({
  color: theme.palette.warning.main,
  ...collapsingButtonStyles(visible),
}));

const KpiRestoreIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible: boolean }>(({ theme, visible }) => ({
  color: theme.palette.primary.main,
  ...collapsingButtonStyles(visible),
}));

interface Props {
  groupBy: string;
  groupByOptions: GroupByOption[];
  onGroupByChange: (next: string) => void;
  view: AnalyticsView;
  onViewChange: (next: AnalyticsView) => void;
  onExport?: (format?: string, scope?: ExportScope) => Promise<AxiosResponse<Blob>>;
  exportOptions?: ExportOption[];
  paginated?: boolean;
  isDrillDown?: boolean;
  onFullscreen?: () => void;
  onSaveAsTile?: () => void;
  hasNotice?: boolean;
  noticeDismissed?: boolean;
  onNoticeRestore?: () => void;
  kpiDismissed?: boolean;
  onKpiRestore?: () => void;
  columnPicker?: ReactNode;
}

export const AnalyticsResultToolbar: FC<Props> = ({
  groupBy,
  groupByOptions,
  onGroupByChange,
  view,
  onViewChange,
  onExport,
  exportOptions,
  paginated,
  isDrillDown,
  onFullscreen,
  onSaveAsTile,
  hasNotice,
  noticeDismissed,
  onNoticeRestore,
  kpiDismissed,
  onKpiRestore,
  columnPicker,
}) => {
  const { t } = useTranslation();
  const exportScopes = paginated ? PAGINATED_EXPORT_SCOPES : UNPAGINATED_EXPORT_SCOPES;
  const showRestoreNotice = !!hasNotice && !!noticeDismissed;
  const showRestoreKpi = !!kpiDismissed;
  return (
    <ChartToolbar>
      {view === 'table' && !isDrillDown && (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t('analytics.filter.tableGroupByLabel')}</InputLabel>
          <QcsDropdownMenu
            value={groupBy}
            label={t('analytics.filter.tableGroupByLabel')}
            onChange={(e) => onGroupByChange(e.target.value as string)}
            items={buildGroupByDropdownItems(groupByOptions, t)}
          />
        </FormControl>
      )}

      <ChartToolbarTrailing>
        {view === 'table' && onExport && (
          <QcsExportButton onExport={onExport} options={exportOptions} scopes={exportScopes} variant="text" />
        )}
        {view === 'table' && columnPicker}
        {hasNotice && (
          <Tooltip title={t('analytics.notice.title')} arrow>
            <WarningIconButton size="small" visible={showRestoreNotice} onClick={onNoticeRestore}>
              <WarningAmberRoundedIcon fontSize="small" />
            </WarningIconButton>
          </Tooltip>
        )}
        {onKpiRestore && (
          <Tooltip title={t('analytics.kpi.show')} arrow>
            <KpiRestoreIconButton size="small" visible={showRestoreKpi} onClick={onKpiRestore}>
              <BarChartIcon fontSize="small" />
            </KpiRestoreIconButton>
          </Tooltip>
        )}
        <AnalyticsViewToggle value={view} onChange={onViewChange} />
        {onSaveAsTile && (
          <Tooltip title={t('analytics.tile.saveAsTile')}>
            <IconButton size="small" onClick={onSaveAsTile}>
              <DashboardCustomizeOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onFullscreen && (
          <Tooltip title={t('analytics.chart.fullscreen')}>
            <IconButton size="small" onClick={onFullscreen}>
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </ChartToolbarTrailing>
    </ChartToolbar>
  );
};
